import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  theme,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Row,
  Col,
  Modal,
  Tabs,
  Typography,
  Dropdown,
  MenuProps,
  Statistic,
  DatePicker,
  Form,
  InputNumber,
  App,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  MoreOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Subscription,
  SubscriptionStatus,
  ChargeInquiryStatus,
  InvoiceStatus,
  SubscriptionFilters,
  ChargeInquiryFilters,
  InvoiceFilters,
  SubscriptionWithUser,
  ChargeInquiryWithUser,
  InvoiceWithUser,
} from '@mygate/common';
import { subscriptionService } from '@/services/subscription';
import { usersService } from '@/services/users';
import { UserDetailModal } from '@/components/UserDetailModal';
import { AutoBillingRunsTab } from '@/components/AutoBillingRunsTab';
import { ColumnsType } from 'antd/es/table';
import { formatDateTime } from '@/utils/date';
import { formatCurrency, formatVATRate, formatCountryCode } from '@/utils/currency';
import { calculateVAT, EU_COUNTRIES, NON_EU_COUNTRIES, VATBreakdown } from '@/utils/vat';
import { VATPreview } from '@/components/VATPreview';
import dayjs from 'dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export function SubscriptionsPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { modal, message } = App.useApp();
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [generateChargesModalVisible, setGenerateChargesModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [selectedSubscriptionForCharge, setSelectedSubscriptionForCharge] = useState<Subscription | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithUser | null>(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [filters, setFilters] = useState<SubscriptionFilters>({});
  const [chargeFilters, setChargeFilters] = useState<ChargeInquiryFilters>({});
  const [invoiceFilters, setInvoiceFilters] = useState<InvoiceFilters>({});
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [chargeForm] = Form.useForm();

  // UserDetailModal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // VAT calculation state
  const [createVATBreakdown, setCreateVATBreakdown] = useState<VATBreakdown | null>(null);

  // CSV export state
  const [exportingCsv, setExportingCsv] = useState(false);
  const [editVATBreakdown, setEditVATBreakdown] = useState<VATBreakdown | null>(null);

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['subscription-stats'],
    queryFn: async () => {
      const response = await subscriptionService.getStatistics();
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Fetch subscriptions
  const {
    data: subscriptions = [],
    isLoading: subscriptionsLoading,
    refetch: refetchSubscriptions,
  } = useQuery({
    queryKey: ['subscriptions', filters],
    queryFn: async () => {
      const response = await subscriptionService.listSubscriptions(filters);
      return response.data?.items || [];
    },
  });

  // Fetch charge inquiries
  const {
    data: charges = [],
    isLoading: chargesLoading,
    refetch: refetchCharges,
  } = useQuery({
    queryKey: ['charge-inquiries', chargeFilters],
    queryFn: async () => {
      const response = await subscriptionService.listChargeInquiries(chargeFilters);
      return response.data?.items || [];
    },
    enabled: activeTab === 'charges',
  });

  // Fetch invoices
  const {
    data: invoices = [],
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['invoices', invoiceFilters],
    queryFn: async () => {
      const response = await subscriptionService.listInvoices(invoiceFilters);
      return response.data?.items || [];
    },
    enabled: activeTab === 'invoices',
  });

  // Fetch users for dropdown
  const { data: users = [] } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const response = await usersService.fetchAllUsers();
      return response.data?.items || [];
    },
  });

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async (values: any) => {
      return subscriptionService.createSubscription({
        userId: values.userId,
        plan: values.plan,
        monthlyAmount: values.monthlyAmount,
        startDate: values.startDate.format('YYYY-MM-DD'), // Send as simple date string
        notes: values.notes,
        country: values.country, // Customer country for VAT calculation
        netAmount: createVATBreakdown?.netAmount,
        vatRate: createVATBreakdown?.vatRate,
        vatAmount: createVATBreakdown?.vatAmount,
      });
    },
    onSuccess: () => {
      message.success('Subscription created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      setCreateVATBreakdown(null);
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to create subscription');
    },
  });

  // Generate charges mutation
  const generateChargesMutation = useMutation({
    mutationFn: async (values: any) => {
      const subscriptionIds = selectedRowKeys.length > 0 ? selectedRowKeys : values.subscriptionIds;
      return subscriptionService.generateChargeInquiries({
        subscriptionIds,
        billingPeriodStart: values.period[0].format('YYYY-MM-DD'), // Send as simple date string
        billingPeriodEnd: values.period[1].format('YYYY-MM-DD'), // Send as simple date string
        notes: values.notes,
      });
    },
    onSuccess: response => {
      const data = response.data;
      message.success(`Generated ${data?.generated} charges for total amount €${data?.totalAmount?.toFixed(2)}`);
      setGenerateChargesModalVisible(false);
      chargeForm.resetFields();
      setSelectedRowKeys([]);
      setSelectedSubscriptionForCharge(null);
      queryClient.invalidateQueries({ queryKey: ['charge-inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
      setActiveTab('charges');
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to generate charges');
    },
  });

  // Bulk approve charges mutation
  const bulkApproveChargesMutation = useMutation({
    mutationFn: (chargeIds: string[]) => subscriptionService.bulkApproveCharges(chargeIds),
    onSuccess: () => {
      message.success('Charges approved successfully');
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({ queryKey: ['charge-inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to approve charges');
    },
  });

  // Update subscription mutation
  const updateSubscriptionMutation = useMutation({
    mutationFn: (values: any) => {
      const updateData = {
        ...values,
        country: values.country, // Update customer country
        netAmount: editVATBreakdown?.netAmount,
        vatRate: editVATBreakdown?.vatRate,
        vatAmount: editVATBreakdown?.vatAmount,
      };
      return subscriptionService.updateSubscription(editingSubscription!.id, updateData);
    },
    onSuccess: () => {
      message.success('Subscription updated successfully');
      setEditModalVisible(false);
      setEditingSubscription(null);
      editForm.resetFields();
      setEditVATBreakdown(null);
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to update subscription');
    },
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (id: string) => {
      return await subscriptionService.cancelSubscription(id);
    },
    onSuccess: () => {
      message.success('Subscription cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-stats'] });
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to cancel subscription');
    },
  });

  // Calculate smart billing period based on subscription start date
  const calculateBillingPeriod = (subscription: Subscription | null): [dayjs.Dayjs, dayjs.Dayjs] => {
    if (!subscription) {
      // Default: current month
      return [dayjs().startOf('month'), dayjs().endOf('month')];
    }

    const contractStart = dayjs(subscription.startDate);
    const now = dayjs();
    const currentMonthStart = now.startOf('month');

    // Check if subscription started in current month
    if (contractStart.isSame(now, 'month')) {
      // Use contract start date as period start
      return [contractStart, now.endOf('month')];
    }

    // Subscription from previous month(s) - use current month start
    return [currentMonthStart, now.endOf('month')];
  };

  // Handle VAT calculation for Create modal
  const handleCreateVATCalculation = () => {
    const country = form.getFieldValue('country');
    const amount = form.getFieldValue('monthlyAmount');

    if (country && amount > 0) {
      const breakdown = calculateVAT(amount, country);
      setCreateVATBreakdown(breakdown);
    } else {
      setCreateVATBreakdown(null);
    }
  };

  // Handle user selection in Create modal (auto-fill country)
  const handleUserSelect = (userId: string) => {
    const selectedUser = users.find(u => u.id === userId);
    if (selectedUser?.country) {
      form.setFieldsValue({ country: selectedUser.country });
      handleCreateVATCalculation();
    }
  };

  // Handle VAT calculation for Edit modal
  const handleEditVATCalculation = () => {
    const country = editForm.getFieldValue('country');
    const amount = editForm.getFieldValue('monthlyAmount');

    if (country && amount > 0) {
      const breakdown = calculateVAT(amount, country);
      setEditVATBreakdown(breakdown);
    } else {
      setEditVATBreakdown(null);
    }
  };

  // Handle view user details
  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setDetailModalVisible(true);
  };

  // Subscription columns
  const subscriptionColumns: ColumnsType<SubscriptionWithUser> = [
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 185,
      render: (_: string, record: SubscriptionWithUser) => {
        if (!record.user) return record.userId;
        const name = `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim();
        const displayName = name || record.user.phoneNumber || record.user.email || record.userId;

        return (
          <Button type="link" onClick={() => handleViewDetails(record.userId)} style={{ padding: 0, height: 'auto' }}>
            <Space wrap>
              <UserOutlined />
              <Text strong style={{ color: token.colorPrimary }}>
                {displayName}
              </Text>
            </Space>
          </Button>
        );
      },
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 85,
      render: (plan: string) => <Tag color={plan === 'SMART' ? 'blue' : 'default'}>{plan}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 135,
      render: (status: SubscriptionStatus) => {
        const colors: Record<SubscriptionStatus, string> = {
          [SubscriptionStatus.ACTIVE]: 'green',
          [SubscriptionStatus.SUSPENDED]: 'orange',
          [SubscriptionStatus.CANCELLED]: 'red',
          [SubscriptionStatus.PENDING_PAYMENT]: 'gold',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: 'Monatlicher Betrag',
      key: 'amountBreakdown',
      width: 210,
      align: 'right',
      render: (_, record) => (
        <div className="table-cell-stack table-cell-numeric">
          <Text strong>{formatCurrency(record.monthlyAmount)} brutto</Text>
          <Text className="table-cell-muted">Netto: {formatCurrency(record.netAmount)}</Text>
          <Text className="table-cell-muted">
            USt. {formatVATRate(record.vatRate)}: {formatCurrency(record.vatAmount)}
          </Text>
          <Text className="table-cell-muted">{formatCountryCode(record.customerCountry, true)}</Text>
        </div>
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 135,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Next Billing',
      dataIndex: 'nextBillingDate',
      key: 'nextBillingDate',
      width: 135,
      render: (date: string) => (date ? formatDateTime(date) : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 135,
      render: (_: any, record: SubscriptionWithUser) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Edit Subscription',
            disabled: record.status === SubscriptionStatus.CANCELLED,
            onClick: () => {
              if (record.status === SubscriptionStatus.CANCELLED) {
                message.warning('Cannot edit a cancelled subscription');
                return;
              }
              setEditingSubscription(record);
              editForm.setFieldsValue({
                plan: record.plan,
                status: record.status,
                monthlyAmount: record.monthlyAmount,
                country: record.customerCountry || '',
                notes: record.notes || '',
                suspendedReason: record.suspendedReason || '',
              });
              // Calculate VAT with existing values
              if (record.customerCountry && record.monthlyAmount) {
                const breakdown = calculateVAT(record.monthlyAmount, record.customerCountry);
                setEditVATBreakdown(breakdown);
              }
              setEditModalVisible(true);
            },
          },
          {
            key: 'generate',
            label: 'Generate Charge',
            disabled: record.status === SubscriptionStatus.CANCELLED || record.status === SubscriptionStatus.SUSPENDED,
            onClick: () => {
              if (record.status === SubscriptionStatus.CANCELLED || record.status === SubscriptionStatus.SUSPENDED) {
                message.warning('Cannot generate charges for cancelled or suspended subscriptions');
                return;
              }
              setSelectedRowKeys([record.id]);
              setSelectedSubscriptionForCharge(record);
              setGenerateChargesModalVisible(true);
            },
          },
          {
            type: 'divider',
          },
          {
            key: 'cancel',
            label: 'Cancel Subscription',
            danger: true,
            disabled: record.status === SubscriptionStatus.CANCELLED,
            onClick: () => {
              if (record.status === SubscriptionStatus.CANCELLED) {
                message.warning('Subscription is already cancelled');
                return;
              }

              modal.confirm({
                title: 'Cancel Subscription',
                content: 'Are you sure you want to cancel this subscription? This action cannot be undone.',
                okText: 'Yes, Cancel',
                cancelText: 'No, Keep',
                okType: 'danger',
                onOk: async () => {
                  try {
                    await cancelSubscriptionMutation.mutateAsync(record.id);
                  } catch (error) {
                    // Error is handled by the mutation's onError
                  }
                },
              });
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button aria-label="Abo-Aktionen" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Charge inquiry columns
  const chargeColumns: ColumnsType<ChargeInquiryWithUser> = [
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 185,
      render: (_: string, record: ChargeInquiryWithUser) => {
        if (!record.user) return record.userId;
        const name = `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim();
        const displayName = name || record.user.phoneNumber || record.user.email || record.userId;

        return (
          <Button type="link" onClick={() => handleViewDetails(record.userId)} style={{ padding: 0, height: 'auto' }}>
            <Space wrap>
              <UserOutlined />
              <Text strong style={{ color: token.colorPrimary }}>
                {displayName}
              </Text>
            </Space>
          </Button>
        );
      },
    },
    {
      title: 'Period',
      key: 'period',
      render: (_: any, record: ChargeInquiryWithUser) => {
        const start = new Date(record.billingPeriodStart);
        const end = new Date(record.billingPeriodEnd);
        return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
      },
    },
    {
      title: 'Betrag',
      key: 'amountBreakdown',
      width: 210,
      align: 'right',
      render: (_, record) => (
        <div className="table-cell-stack table-cell-numeric">
          <Text strong>{formatCurrency(record.grossAmount ?? record.amount)} brutto</Text>
          <Text className="table-cell-muted">Netto: {formatCurrency(record.netAmount)}</Text>
          <Text className="table-cell-muted">
            USt. {formatVATRate(record.vatRate)}: {formatCurrency(record.vatAmount)}
          </Text>
          <Text className="table-cell-muted">{formatCountryCode(record.customerCountry, true)}</Text>
          {record.isProrated && <Tag color="blue">Prorated</Tag>}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 135,
      render: (status: ChargeInquiryStatus) => {
        const colors: Record<ChargeInquiryStatus, string> = {
          [ChargeInquiryStatus.PENDING]: 'gold',
          [ChargeInquiryStatus.APPROVED]: 'blue',
          [ChargeInquiryStatus.PROCESSING]: 'processing',
          [ChargeInquiryStatus.COMPLETED]: 'green',
          [ChargeInquiryStatus.FAILED]: 'red',
          [ChargeInquiryStatus.CANCELLED]: 'default',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 135,
      render: (_: any, record: ChargeInquiryWithUser) => {
        if (record.status === ChargeInquiryStatus.PENDING) {
          return (
            <Space wrap>
              <Button
                type="primary"
                size="small"
                onClick={async () => {
                  try {
                    await subscriptionService.approveChargeInquiry(record.id);
                    message.success('Charge approved');
                    queryClient.invalidateQueries({ queryKey: ['charge-inquiries'] });
                  } catch (error: any) {
                    message.error(error.message || 'Failed to approve charge');
                  }
                }}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                onClick={async () => {
                  try {
                    await subscriptionService.cancelChargeInquiry(record.id);
                    message.success('Charge cancelled');
                    queryClient.invalidateQueries({ queryKey: ['charge-inquiries'] });
                  } catch (error: any) {
                    message.error(error.message || 'Failed to cancel charge');
                  }
                }}
              >
                Cancel
              </Button>
            </Space>
          );
        }
        return record.transactionQueueId ? (
          <Button size="small" onClick={() => navigate(`/transaction-queue?id=${record.transactionQueueId}`)}>
            View Transaction
          </Button>
        ) : null;
      },
    },
  ];

  // Invoice columns
  const invoiceColumns: ColumnsType<InvoiceWithUser> = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 135,
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 185,
      render: (_: string, record: InvoiceWithUser) => {
        if (!record.user) return record.userId;
        const name = `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim();
        const displayName = name || record.user.phoneNumber || record.user.email || record.userId;

        return (
          <Button type="link" onClick={() => handleViewDetails(record.userId)} style={{ padding: 0, height: 'auto' }}>
            <Space wrap>
              <UserOutlined />
              <Text strong style={{ color: token.colorPrimary }}>
                {displayName}
              </Text>
            </Space>
          </Button>
        );
      },
    },
    {
      title: 'Period',
      dataIndex: 'billingPeriod',
      key: 'billingPeriod',
    },
    {
      title: 'Betrag',
      key: 'amountBreakdown',
      width: 210,
      align: 'right',
      render: (_, record) => (
        <div className="table-cell-stack table-cell-numeric">
          <Text strong>{formatCurrency(record.grossAmount ?? record.totalAmount)} brutto</Text>
          <Text className="table-cell-muted">Netto: {formatCurrency(record.netAmount ?? record.amount)}</Text>
          <Text className="table-cell-muted">
            USt. {formatVATRate(record.vatRate)}: {formatCurrency(record.vatAmount ?? record.tax)}
          </Text>
          <Text className="table-cell-muted">{formatCountryCode(record.customerCountry, true)}</Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 135,
      render: (status: InvoiceStatus) => {
        const colors: Record<InvoiceStatus, string> = {
          [InvoiceStatus.DRAFT]: 'default',
          [InvoiceStatus.ISSUED]: 'blue',
          [InvoiceStatus.PAID]: 'green',
          [InvoiceStatus.CANCELLED]: 'red',
          [InvoiceStatus.REFUNDED]: 'orange',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      },
    },
    {
      title: 'Issued Date',
      dataIndex: 'issuedDate',
      key: 'issuedDate',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 135,
      render: (_: any, record: InvoiceWithUser) => (
        <Space wrap>
          <Button
            size="small"
            onClick={() => {
              setSelectedInvoice(record);
              setInvoiceModalVisible(true);
            }}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={async () => {
              try {
                await subscriptionService.downloadInvoicePDF(record.id);
                message.success('Invoice PDF downloaded successfully');
              } catch (error: any) {
                message.error(error.message || 'Failed to download invoice PDF');
              }
            }}
          >
            Download PDF
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Abonnements, Belastungen und Rechnungen verwalten.</Typography.Text>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            if (activeTab === 'subscriptions') refetchSubscriptions();
            else if (activeTab === 'charges') refetchCharges();
            else if (activeTab === 'invoices') refetchInvoices();
          }}
        >
          Refresh
        </Button>
      </div>

      <>
        {/* Statistics */}
        <Row className="operational-stats" gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} xl={6}>
            <Card>
              <Statistic
                title="Active Subscriptions"
                value={stats?.activeSubscriptions || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card>
              <Statistic
                title="Monatlicher Abo-Bestand (SMART)"
                value={stats?.monthlyRevenue || 0}
                prefix="€"
                precision={2}
                valueStyle={{ color: token.colorPrimary }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card>
              <Statistic
                title="Pending Charges"
                value={stats?.pendingCharges || 0}
                suffix={`(€${stats?.pendingChargesAmount?.toFixed(2) || '0.00'})`}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <Card>
              <Statistic
                title="Failed Charges"
                value={stats?.failedCharges || 0}
                suffix={`(€${stats?.failedChargesAmount?.toFixed(2) || '0.00'})`}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Tabs */}
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'subscriptions',
                label: 'Subscriptions',
                children: (
                  <>
                    <Space wrap style={{ marginBottom: 16 }}>
                      <Input.Search
                        placeholder="Search by name..."
                        style={{ width: 200 }}
                        allowClear
                        onSearch={value => setFilters({ ...filters, search: value || undefined })}
                      />
                      <Select
                        placeholder="Plan"
                        style={{ width: 120 }}
                        allowClear
                        onChange={value => setFilters({ ...filters, plan: value as any })}
                      >
                        <Select.Option value="FREE">FREE</Select.Option>
                        <Select.Option value="SMART">SMART</Select.Option>
                      </Select>
                      <Select
                        placeholder="Status"
                        style={{ width: 150 }}
                        allowClear
                        onChange={value => setFilters({ ...filters, status: value as any })}
                      >
                        <Select.Option value={SubscriptionStatus.ACTIVE}>Active</Select.Option>
                        <Select.Option value={SubscriptionStatus.SUSPENDED}>Suspended</Select.Option>
                        <Select.Option value={SubscriptionStatus.CANCELLED}>Cancelled</Select.Option>
                      </Select>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                        New Subscription
                      </Button>
                      <Button
                        type="default"
                        icon={<DollarOutlined />}
                        onClick={() => {
                          setSelectedSubscriptionForCharge(null);
                          setGenerateChargesModalVisible(true);
                        }}
                        disabled={selectedRowKeys.length === 0}
                      >
                        Generate Charges ({selectedRowKeys.length})
                      </Button>
                    </Space>
                    <Table
                      tableLayout="fixed"
                      scroll={{ x: 1040 }}
                      columns={subscriptionColumns}
                      dataSource={subscriptions}
                      rowKey="id"
                      loading={subscriptionsLoading}
                      rowSelection={{
                        selectedRowKeys,
                        onChange: selectedKeys => setSelectedRowKeys(selectedKeys.map(String)),
                        getCheckboxProps: record => ({
                          disabled: record.plan !== 'SMART' || record.status !== SubscriptionStatus.ACTIVE,
                        }),
                      }}
                    />
                  </>
                ),
              },
              {
                key: 'charges',
                label: 'Charge Inquiries',
                children: (
                  <>
                    <Space wrap style={{ marginBottom: 16 }}>
                      <Select
                        placeholder="Status"
                        style={{ width: 150 }}
                        allowClear
                        onChange={value => setChargeFilters({ ...chargeFilters, status: value as any })}
                      >
                        <Select.Option value={ChargeInquiryStatus.PENDING}>Pending</Select.Option>
                        <Select.Option value={ChargeInquiryStatus.APPROVED}>Approved</Select.Option>
                        <Select.Option value={ChargeInquiryStatus.COMPLETED}>Completed</Select.Option>
                        <Select.Option value={ChargeInquiryStatus.FAILED}>Failed</Select.Option>
                      </Select>
                      <Button
                        type="primary"
                        disabled={selectedRowKeys.length === 0}
                        onClick={() => bulkApproveChargesMutation.mutate(selectedRowKeys)}
                      >
                        Bulk Approve ({selectedRowKeys.length})
                      </Button>
                    </Space>
                    <Table
                      tableLayout="fixed"
                      scroll={{ x: 1040 }}
                      columns={chargeColumns}
                      dataSource={charges}
                      rowKey="id"
                      loading={chargesLoading}
                      rowSelection={{
                        selectedRowKeys,
                        onChange: selectedKeys => setSelectedRowKeys(selectedKeys.map(String)),
                        getCheckboxProps: record => ({
                          disabled: record.status !== ChargeInquiryStatus.PENDING,
                        }),
                      }}
                    />
                  </>
                ),
              },
              {
                key: 'invoices',
                label: 'Invoices',
                children: (
                  <>
                    <Space wrap style={{ marginBottom: 16 }}>
                      <RangePicker
                        onChange={dates => {
                          if (dates && dates[0] && dates[1]) {
                            setInvoiceFilters({
                              ...invoiceFilters,
                              issuedDateFrom: dates[0].toDate(),
                              issuedDateTo: dates[1].toDate(),
                            });
                          }
                        }}
                      />
                      <Button
                        icon={<DownloadOutlined />}
                        loading={exportingCsv}
                        disabled={!invoiceFilters.issuedDateFrom || !invoiceFilters.issuedDateTo}
                        onClick={async () => {
                          if (!invoiceFilters.issuedDateFrom || !invoiceFilters.issuedDateTo) {
                            message.warning('Please select a date range first');
                            return;
                          }
                          setExportingCsv(true);
                          try {
                            await subscriptionService.exportInvoicesCsv({
                              issuedDateFrom: dayjs(invoiceFilters.issuedDateFrom).format('YYYY-MM-DD'),
                              issuedDateTo: dayjs(invoiceFilters.issuedDateTo).format('YYYY-MM-DD'),
                              status: invoiceFilters.status,
                            });
                            message.success('CSV exported successfully');
                          } catch {
                            message.error('Failed to export CSV');
                          } finally {
                            setExportingCsv(false);
                          }
                        }}
                      >
                        Export CSV
                      </Button>
                    </Space>
                    <Table
                      tableLayout="fixed"
                      scroll={{ x: 1040 }}
                      columns={invoiceColumns}
                      dataSource={invoices}
                      rowKey="id"
                      loading={invoicesLoading}
                    />
                  </>
                ),
              },
              {
                key: 'auto-billing',
                label: 'Auto-Billing',
                children: <AutoBillingRunsTab />,
              },
            ]}
          />
        </Card>

        {/* Create Subscription Modal */}
        <Modal
          title="Create Subscription"
          open={createModalVisible}
          onCancel={() => {
            setCreateModalVisible(false);
            form.resetFields();
            setCreateVATBreakdown(null);
          }}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={createSubscriptionMutation.mutate}>
            <Form.Item name="userId" label="User" rules={[{ required: true, message: 'Please select a user' }]}>
              <Select
                showSearch
                placeholder="Select a user"
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                onChange={handleUserSelect}
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.firstName || ''} ${user.lastName || ''} (${user.phoneNumber})`.trim(),
                }))}
              />
            </Form.Item>
            <Form.Item name="plan" label="Plan" rules={[{ required: true }]} initialValue="SMART">
              <Select>
                <Select.Option value="FREE">FREE</Select.Option>
                <Select.Option value="SMART">SMART</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="monthlyAmount" label="Monthly Amount (€)" rules={[{ required: true }]} initialValue={8.9}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} onChange={handleCreateVATCalculation} />
            </Form.Item>
            <Form.Item
              name="country"
              label="Customer Country"
              rules={[{ required: true, message: 'Please select customer country' }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                onChange={handleCreateVATCalculation}
              >
                <Select.OptGroup label="🇪🇺 EU Countries (with VAT)">
                  {EU_COUNTRIES.map(country => (
                    <Select.Option key={country.code} value={country.code}>
                      {country.label}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
                <Select.OptGroup label="🌍 Non-EU Countries (0% VAT)">
                  {NON_EU_COUNTRIES.map(country => (
                    <Select.Option key={country.code} value={country.code}>
                      {country.label}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              </Select>
            </Form.Item>
            <Form.Item label="VAT Breakdown">
              <VATPreview vatBreakdown={createVATBreakdown} />
            </Form.Item>
            <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]} initialValue={dayjs()}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={createSubscriptionMutation.isPending}>
                  Create
                </Button>
                <Button onClick={() => setCreateModalVisible(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Generate Charges Modal */}
        <Modal
          title="Generate Monthly Charges"
          open={generateChargesModalVisible}
          onCancel={() => {
            setGenerateChargesModalVisible(false);
            setSelectedSubscriptionForCharge(null);
          }}
          footer={null}
          width={600}
        >
          <Form form={chargeForm} layout="vertical" onFinish={generateChargesMutation.mutate}>
            <Form.Item
              name="period"
              label="Billing Period"
              rules={[{ required: true, message: 'Please select billing period' }]}
              initialValue={calculateBillingPeriod(selectedRowKeys.length === 1 ? selectedSubscriptionForCharge : null)}
            >
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
            {selectedRowKeys.length === 0 && (
              <Form.Item
                name="subscriptionIds"
                label="Select Subscriptions"
                rules={[{ required: true, message: 'Please select subscriptions' }]}
              >
                <Select mode="multiple" placeholder="Select subscriptions to charge" style={{ width: '100%' }}>
                  {subscriptions
                    .filter(s => s.plan === 'SMART' && s.status === SubscriptionStatus.ACTIVE)
                    .map(sub => {
                      const user = users.find(u => u.id === sub.userId);
                      return (
                        <Select.Option key={sub.id} value={sub.id}>
                          {user ? `${user.firstName || ''} ${user.lastName || ''} (${user.phoneNumber})` : sub.userId} -
                          €{sub.monthlyAmount}
                        </Select.Option>
                      );
                    })}
                </Select>
              </Form.Item>
            )}
            {selectedRowKeys.length > 0 && (
              <Form.Item label="Selected Subscriptions">
                <div>{selectedRowKeys.length} subscriptions selected</div>
              </Form.Item>
            )}
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={generateChargesMutation.isPending}>
                  Generate Charges
                </Button>
                <Button
                  onClick={() => {
                    setGenerateChargesModalVisible(false);
                    setSelectedSubscriptionForCharge(null);
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Invoice Detail Modal */}
        <Modal
          title={`Invoice ${selectedInvoice?.invoiceNumber}`}
          open={invoiceModalVisible}
          onCancel={() => {
            setInvoiceModalVisible(false);
            setSelectedInvoice(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setInvoiceModalVisible(false);
                setSelectedInvoice(null);
              }}
            >
              Close
            </Button>,
          ]}
          width={600}
        >
          {selectedInvoice && (
            <div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}
                </Col>
                <Col xs={24} md={12}>
                  <strong>Status:</strong>{' '}
                  <Tag color={selectedInvoice.status === InvoiceStatus.PAID ? 'green' : 'default'}>
                    {selectedInvoice.status}
                  </Tag>
                </Col>

                {/* Seller Information */}
                <Col xs={24} md={24}>
                  <strong>From:</strong>
                </Col>
                <Col xs={24} md={24} style={{ paddingLeft: 16, marginBottom: 16 }}>
                  {selectedInvoice.seller?.name || 'Mondo Gate AG'}
                  <br />
                  {selectedInvoice.seller?.address}
                  {selectedInvoice.seller?.taxId && (
                    <>
                      <br />
                      USt. ID: {selectedInvoice.seller.taxId}
                    </>
                  )}
                  {selectedInvoice.seller?.registrationNumber && selectedInvoice.seller?.registrationCourt && (
                    <>
                      <br />
                      Registernummer: {selectedInvoice.seller.registrationNumber} |{' '}
                      {selectedInvoice.seller.registrationCourt}
                    </>
                  )}
                </Col>

                {/* Buyer Information */}
                <Col xs={24} md={24}>
                  <strong>To:</strong>
                </Col>
                <Col xs={24} md={24} style={{ paddingLeft: 16, marginBottom: 16 }}>
                  {selectedInvoice.buyer?.name}
                  <br />
                  {selectedInvoice.buyer?.email}
                  <br />
                  {selectedInvoice.buyer?.phone}
                  {selectedInvoice.buyer?.customerId && (
                    <>
                      <br />
                      Customer ID: {selectedInvoice.buyer.customerId}
                    </>
                  )}
                </Col>

                {/* Line Items */}
                <Col xs={24} md={24}>
                  <strong>Items:</strong>
                </Col>
                <Col xs={24} md={24} style={{ paddingLeft: 16, marginBottom: 16 }}>
                  {selectedInvoice.lineItems?.map(
                    (
                      item: { description: string; quantity: number; unitPrice: number; totalPrice: number },
                      index: number,
                    ) => (
                      <div key={index}>
                        {item.description}
                        <br />
                        Qty: {item.quantity} × €{item.unitPrice.toFixed(2)} = €{item.totalPrice.toFixed(2)}
                      </div>
                    ),
                  )}
                </Col>

                {/* VAT Breakdown */}
                {(selectedInvoice.netAmount || selectedInvoice.vatAmount || selectedInvoice.vatRate) && (
                  <>
                    <Col xs={24} md={24} style={{ marginTop: 16 }}>
                      <strong>VAT Details:</strong>
                    </Col>
                    <Col xs={24} md={12}>
                      <strong>Net Amount:</strong> {formatCurrency(selectedInvoice.netAmount || selectedInvoice.amount)}
                    </Col>
                    <Col xs={24} md={12}>
                      <strong>VAT Rate:</strong> {formatVATRate(selectedInvoice.vatRate)}
                    </Col>
                    <Col xs={24} md={12}>
                      <strong>VAT Amount:</strong> {formatCurrency(selectedInvoice.vatAmount || selectedInvoice.tax)}
                    </Col>
                    <Col xs={24} md={12}>
                      <strong>Gross Amount:</strong>{' '}
                      {formatCurrency(selectedInvoice.grossAmount || selectedInvoice.totalAmount)}
                    </Col>
                    {selectedInvoice.customerCountry && (
                      <Col xs={24} md={12}>
                        <strong>Tax Country:</strong> {formatCountryCode(selectedInvoice.customerCountry, true)}
                      </Col>
                    )}
                  </>
                )}

                <Col xs={24} md={12}>
                  <strong>Billing Period:</strong> {selectedInvoice.billingPeriod}
                </Col>
                <Col xs={24} md={12}>
                  <strong>Total Amount:</strong> €{selectedInvoice.totalAmount.toFixed(2)}
                </Col>
                <Col xs={24} md={12}>
                  <strong>Issued Date:</strong> {formatDateTime(selectedInvoice.issuedDate)}
                </Col>
                {selectedInvoice.deliveryDate && (
                  <Col xs={24} md={12}>
                    <strong>Delivery Date:</strong> {formatDateTime(selectedInvoice.deliveryDate)}
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <strong>Payment Method:</strong> {selectedInvoice.paymentMethod}
                </Col>
                {selectedInvoice.paidDate && (
                  <Col xs={24} md={12}>
                    <strong>Paid Date:</strong> {formatDateTime(selectedInvoice.paidDate)}
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <strong>Currency:</strong> {selectedInvoice.currency}
                </Col>
              </Row>
            </div>
          )}
        </Modal>

        {/* Edit Subscription Modal */}
        <Modal
          title="Edit Subscription"
          open={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setEditingSubscription(null);
            editForm.resetFields();
            setEditVATBreakdown(null);
          }}
          footer={null}
        >
          <Form form={editForm} layout="vertical" onFinish={updateSubscriptionMutation.mutate}>
            <Form.Item name="plan" label="Plan" rules={[{ required: true, message: 'Please select a plan' }]}>
              <Select>
                <Select.Option value="FREE">FREE</Select.Option>
                <Select.Option value="SMART">SMART</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select a status' }]}>
              <Select>
                <Select.Option value={SubscriptionStatus.ACTIVE}>ACTIVE</Select.Option>
                <Select.Option value={SubscriptionStatus.SUSPENDED}>SUSPENDED</Select.Option>
                <Select.Option value={SubscriptionStatus.PENDING_PAYMENT}>PENDING_PAYMENT</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="monthlyAmount"
              label="Monthly Amount (€)"
              rules={[{ required: true, message: 'Please enter monthly amount' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} onChange={handleEditVATCalculation} />
            </Form.Item>
            <Form.Item
              name="country"
              label="Customer Country"
              rules={[{ required: true, message: 'Please select customer country' }]}
            >
              <Select
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                onChange={handleEditVATCalculation}
              >
                <Select.OptGroup label="🇪🇺 EU Countries (with VAT)">
                  {EU_COUNTRIES.map(country => (
                    <Select.Option key={country.code} value={country.code}>
                      {country.label}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
                <Select.OptGroup label="🌍 Non-EU Countries (0% VAT)">
                  {NON_EU_COUNTRIES.map(country => (
                    <Select.Option key={country.code} value={country.code}>
                      {country.label}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              </Select>
            </Form.Item>
            <Form.Item label="VAT Breakdown">
              <VATPreview vatBreakdown={editVATBreakdown} />
            </Form.Item>
            <Form.Item name="suspendedReason" label="Suspended Reason" dependencies={['status']}>
              <Input.TextArea
                placeholder="Required if status is SUSPENDED"
                disabled={editForm.getFieldValue('status') !== SubscriptionStatus.SUSPENDED}
              />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={updateSubscriptionMutation.isPending}>
                  Update Subscription
                </Button>
                <Button
                  onClick={() => {
                    setEditModalVisible(false);
                    setEditingSubscription(null);
                    editForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* User Detail Modal */}
        <UserDetailModal
          userId={selectedUserId}
          visible={detailModalVisible}
          onClose={() => {
            setDetailModalVisible(false);
            setSelectedUserId(null);
          }}
          onUserUpdated={refetchSubscriptions}
        />
      </>
    </>
  );
}
